/**
 * @file index.tsx
 *
 * @fileoverview Records candidate engagement with job alert emails. Job alert
 * emails carry the job alert id and an activity token on all job board links.
 * When such a link is opened, the engagement is reported to the backend,
 * which resets the adaptive email frequency of the job alert.
 */
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import TRACK_JOB_ALERT_ACTIVITY from '~/jobiqo/components/JobAlert/graphql/mutations/trackJobAlertActivity.gql';
import { useConfig } from '~/jobiqo/context/config';

export const JobAlertActivityTracking = () => {
  const router = useRouter();
  // The adaptive frequency is opt-in per frontend, without it there is no
  // engagement to record.
  const { getConfigByName } = useConfig();
  const adaptiveFrequencyEnabled = !!getConfigByName('adaptive_enabled')
    ?.value[0];
  // Guards against double firing within the same mount, e.g. for browsers
  // where session storage is unavailable.
  const fired = useRef(false);
  const [trackJobAlertActivity] = useMutation(TRACK_JOB_ALERT_ACTIVITY);

  const rawId = router.query.job_alert_id;
  const rawToken = router.query.job_alert_token;
  const id = Array.isArray(rawId) ? rawId[0] : rawId;
  const token = Array.isArray(rawToken) ? rawToken[0] : rawToken;

  // Using useEffect so the mutation only fires client-side: email security
  // scanners prefetching links usually do not execute javascript, so they do
  // not register false engagement.
  useEffect(() => {
    if (
      !adaptiveFrequencyEnabled ||
      !id ||
      !token ||
      Number.isNaN(Number(id))
    ) {
      return;
    }

    const storageKey = `job-alert-activity-${id}`;
    if (!fired.current && !sessionStorage.getItem(storageKey)) {
      fired.current = true;
      sessionStorage.setItem(storageKey, '1');
      // Fire and forget, activity tracking must never break the page.
      trackJobAlertActivity({
        variables: { id: Number(id), token },
      }).catch(() => null);
    }

    // Drop the tracking parameters from the URL, so reloading or sharing the
    // link does not count as engagement again.
    const url = new URL(router.asPath, window.location.origin);
    url.searchParams.delete('job_alert_id');
    url.searchParams.delete('job_alert_token');
    router.replace(url.pathname + url.search + url.hash, undefined, {
      shallow: true,
    });
  }, [adaptiveFrequencyEnabled, id, token]);

  return null;
};

export default JobAlertActivityTracking;
